import { z } from "zod";

/** ISO 8601 instant in UTC with a `Z` suffix. */
export const Instant = z.iso.datetime({ offset: false });
export type Instant = z.infer<typeof Instant>;

/** Calendar date, `YYYY-MM-DD`, no time zone. */
export const CalendarDate = z.iso.date();
export type CalendarDate = z.infer<typeof CalendarDate>;

export const EntityId = z.uuid();
export type EntityId = z.infer<typeof EntityId>;

/** Fields every synchronized entity carries. Never omit them. */
/** Free-form labels. Stored trimmed, de-duplicated, without a leading `#` or `@`. */
export const Tags = z.array(z.string().min(1)).max(50);
export type Tags = z.infer<typeof Tags>;

export const Synced = z.object({
  id: EntityId,
  createdAt: Instant,
  updatedAt: Instant,
  deletedAt: Instant.optional(),
  /** Server-assigned, monotonically increasing per entity. 0 until first acknowledged. */
  revision: z.number().int().nonnegative(),
});
export type Synced = z.infer<typeof Synced>;

export const Area = Synced.extend({ name: z.string().trim().min(1) });
export type Area = z.infer<typeof Area>;

export const ProjectStatus = z.enum(["active", "archived"]);
export type ProjectStatus = z.infer<typeof ProjectStatus>;

export const Project = Synced.extend({
  name: z.string(),
  /** Optional area of responsibility; projects with the same area name share a sidebar group. */
  area: z.string().trim().min(1).optional(),
  status: ProjectStatus,
  notesFolderId: EntityId.optional(),
  sortOrder: z.number(),
});
export type Project = z.infer<typeof Project>;

export const TaskStatus = z.enum(["open", "completed", "someday"]);
export type TaskStatus = z.infer<typeof TaskStatus>;

export const Priority = z.enum(["low", "medium", "high"]);
export type Priority = z.infer<typeof Priority>;

/**
 * A z-editor document: what the writing surface edits. Only the envelope is checked here; the
 * editor validates the content when it opens it. Stored beside a Markdown mirror of itself, which
 * is what search, titles, links, and export read.
 */
export const EditorDocument = z.object({
  format: z.literal("z-editor"),
  schemaVersion: z.number().int().positive(),
  documentId: z.string().min(1),
  engine: z.object({ name: z.string(), version: z.string() }),
  content: z.record(z.string(), z.unknown()),
});
export type EditorDocument = z.infer<typeof EditorDocument>;

export const Task = Synced.extend({
  title: z.string(),
  /** Markdown mirror of `descriptionDocument`, or the whole description where there is none. */
  description: z.string().optional(),
  /** The description as the editor wrote it. Absent until it is first edited in the editor. */
  descriptionDocument: EditorDocument.optional(),
  /** Source document, retained when a task moves to a project. */
  sourceNoteId: EntityId.optional(),
  /** Explicitly processed out of capture, even when it has no date or project. */
  triaged: z.boolean().optional(),
  /** Calendar day to work on this task, separate from its deadline. */
  plannedDate: CalendarDate.optional(),
  projectId: EntityId.optional(),
  status: TaskStatus,
  priority: Priority.optional(),
  /** "Deadline": a calendar date. Stays the same day when the user travels. */
  dueDate: CalendarDate.optional(),
  /** "Scheduled": an instant the user plans to work on it. */
  scheduledAt: Instant.optional(),
  scheduledDurationMinutes: z.number().int().positive().optional(),
  completedAt: Instant.optional(),
  /** Things-style "This Evening" flag for the Today view. */
  evening: z.boolean().optional(),
  parentTaskId: EntityId.optional(),
  /** Lightweight grouping inside a project. */
  heading: z.string().optional(),
  estimateMinutes: z.number().int().positive().optional(),
  tags: Tags.optional(),
  sortOrder: z.number(),
});
export type Task = z.infer<typeof Task>;

export const Folder = Synced.extend({
  name: z.string(),
  parentId: EntityId.optional(),
  sortOrder: z.number(),
});
export type Folder = z.infer<typeof Folder>;

export const Note = Synced.extend({
  /** Manual position in its Library folder. Absent on older notes. */
  sortOrder: z.number().optional(),
  folderId: EntityId.optional(),
  title: z.string(),
  /** Document this note was extracted from; links remain ordinary Markdown. */
  parentNoteId: EntityId.optional(),
  /**
   * The note as Markdown: a mirror of `document`, written with it on every save, or the whole note
   * where there is no document. Search, titles, links, and export read this.
   */
  markdown: z.string(),
  /** The note as the editor wrote it. Absent until it is first edited in the editor. */
  document: EditorDocument.optional(),
  pinned: z.boolean().optional(),
  /** Parsed YAML frontmatter, stored as JSON for indexing. */
  frontmatter: z.record(z.string(), z.unknown()).optional(),
  tags: Tags.optional(),
});
export type Note = z.infer<typeof Note>;

export const ReminderTarget = z.discriminatedUnion("type", [
  z.object({ type: z.literal("task"), id: EntityId }),
  z.object({ type: z.literal("note"), id: EntityId }),
  z.object({ type: z.literal("standalone") }),
]);
export type ReminderTarget = z.infer<typeof ReminderTarget>;

export const ReminderStatus = z.enum(["scheduled", "delivered", "dismissed", "snoozed"]);
export type ReminderStatus = z.infer<typeof ReminderStatus>;

export const Reminder = Synced.extend({
  target: ReminderTarget,
  title: z.string(),
  triggerAt: Instant,
  status: ReminderStatus,
  snoozedUntil: Instant.optional(),
});
export type Reminder = z.infer<typeof Reminder>;

export const EntityKind = z.enum(["task", "note", "folder", "project", "area", "reminder"]);
export type EntityKind = z.infer<typeof EntityKind>;

export type EntityOf<K extends EntityKind> = K extends "area"
  ? Area
  : K extends "task"
    ? Task
    : K extends "note"
      ? Note
      : K extends "folder"
        ? Folder
        : K extends "project"
          ? Project
          : Reminder;

export type AnyEntity = Task | Note | Folder | Project | Area | Reminder;

export const entitySchema = {
  task: Task,
  note: Note,
  folder: Folder,
  project: Project,
  area: Area,
  reminder: Reminder,
} as const;
